import * as logger from 'loglevel'
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'

import {
  Avatar,
  Box,
  Button,
  CssBaseline,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  Paper,
  Typography
} from '@mui/material'
import PageBackRow from '../UI/PageBackRow'

import UserAuthContainer from '../Containers/UserAuthContainer'
import UIStateContainer from '../Containers/UIStateContainer'
import useNavigation from '../Utils/navigationUtils'
import AuthTokenExpiredError from '../Api/AuthTokenExpiredError'
import anearApi from '../Api/AnearApi'
import { CONTENT_COLUMN_MAX_WIDTH, contentColumnPageShellSx } from '../UI/contentColumn'
import { normalizeAvatarSrc } from '../Utils/avatarSrc'

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const extractAppSlug = (app) => {
  const attrs = app?.app?.attributes || app?.attributes || {}
  return attrs.slug || attrs['slug'] || app?.slug || app?.app?.slug || null
}

const appAttrs = app => {
  const attrs = app.app?.attributes || {}
  return {
    zoneId: app.zone.id,
    appId: app.app.id,
    slug: extractAppSlug(app),
    shortName: attrs['short-name'],
    longName: attrs['long-name'],
    latestAppVersion: attrs['latest-app-version'],
    iconUrl: attrs['icon-url'],
    description: attrs['description'],
    requiresGeoLocation: app.eventRequiresGeoLocation()
  }
}

const appMatchesCreateTarget = (app, createTarget) => {
  if (!app || !createTarget) return false
  return String(app.zoneId) === String(createTarget) || String(extractAppSlug(app)) === String(createTarget)
}

const CreateEventPage = () => {
  const uiStateContainer = UIStateContainer.useContainer()
  const userAuthContainer = UserAuthContainer.useContainer()
  const navigate = useNavigate()

  const { navigateToEvent, navigateToHome, handleAuthTokenExpired } = useNavigation()

  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const participationRadius = useState(null)[0]
  const [disabledSubmitButton, setDisableSubmitButton] = useState(false)
  const [defaultsAreSet, setDefaultsAreSet] = useState(false)
  const [error, setError] = useState(null)
  const [resolvedPlayableApp, setResolvedPlayableApp] = useState(null)
  const [appLookupComplete, setAppLookupComplete] = useState(false)
  const { createTarget } = useParams()

  const { state }  = useLocation()
  const routePlayableApp = state?.playableApp
  const launchFrom = state?.launchFrom || state?.from || null
  const playableApp = appMatchesCreateTarget(routePlayableApp, createTarget)
    ? routePlayableApp
    : resolvedPlayableApp

  const user = userAuthContainer.anearUser()
  const showCreatorAvatarOverlay = Boolean(user) && playableApp?.hosted !== true

  useEffect(() => {
    let cancelled = false

    if (appMatchesCreateTarget(routePlayableApp, createTarget)) {
      setResolvedPlayableApp(routePlayableApp)
      setAppLookupComplete(true)
      return () => { cancelled = true }
    }

    const resolvePlayableApp = async () => {
      if (UUID_PATTERN.test(createTarget)) {
        setResolvedPlayableApp(null)
        setAppLookupComplete(true)
        return
      }

      try {
        const app = await anearApi.getAppBySlug(createTarget)
        if (cancelled) return
        setResolvedPlayableApp(app ? appAttrs(app) : null)
      } catch (lookupError) {
        logger.error(`Unable to resolve create event app '${createTarget}'`, lookupError)
        if (!cancelled) setResolvedPlayableApp(null)
      } finally {
        if (!cancelled) setAppLookupComplete(true)
      }
    }

    setAppLookupComplete(false)
    resolvePlayableApp()

    return () => { cancelled = true }
  }, [routePlayableApp, createTarget])

  useEffect(() => {
    if (!appLookupComplete) return

    // guard in case someone lands here directly without enough context
    if (!playableApp) {
      navigateToHome()
    } else if (user && !defaultsAreSet) {
      const newName = generateDefaultName(playableApp, user)
      const newDescription = generateDefaultDescription(playableApp)

      setName(newName)
      setDescription(newDescription)
      setDefaultsAreSet(true)
    }
  }, [playableApp, user, defaultsAreSet, navigateToHome, appLookupComplete])

  const generateDefaultName = (app, user) => {
    const short_name = app.shortName
    return `${user.attributes.name}'s ${short_name}`
  }

  const generateDefaultDescription = (app) => {
    return app.longName
  }

  const createEventFailure = createEventError => {
    // flash a message in the outline red
    logger.error(createEventError)
    setDisableSubmitButton(false)
    setError(createEventError)
  }

  const handleEventParticipation = async (newEvent) => {
    //
    // the event creator will automatically become a participant
    // in the event via the becomeEventParticipant(newEvent),
    // and have a private display channel, but if the event
    // is hosted, he will not be subscribed to the
    // participants channel
    //
    await anearApi.becomeEventParticipant(newEvent)

    logger.info(`navigating to ${newEvent.slug}`)

    navigateToEvent(
      newEvent.slug,
      launchFrom ? { from: launchFrom } : undefined
    )
  }

  const createEventSuccess = async newEvent => {
    await withProgressIndicator(handleEventParticipation, newEvent)
  }

  const withProgressIndicator = (asyncFunction, ...args) => {
    // always returns Promise
    try {
      uiStateContainer.startProgress()
      return asyncFunction(...args)
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        //
        // Let the shared handler clear stale auth and redirect the user
        // into the appropriate flow (guest-signup or login). We still
        // surface a local error so that any intermediate UI (e.g. this
        // form) does not remain in a confusing state if navigation is
        // interrupted.
        //
        handleAuthTokenExpired(error, userAuthContainer)
      } else {
        createEventFailure(error)
      }
      throw error // Re-throw so callers can add any additional handling
    } finally {
      uiStateContainer.progressComplete()
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setDisableSubmitButton(true)
    setError(null) // Clear any previous errors when retrying

    try {
      if (playableApp.requiresGeoLocation) {
        await withProgressIndicator(userAuthContainer.setPosition)
      }

      const newEvent = await withProgressIndicator(
        anearApi.createEvent,
        playableApp.zoneId,
        {name, description, participationRadius},
        playableApp.requiresGeoLocation
      )
      await createEventSuccess(newEvent)
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        handleAuthTokenExpired(error, userAuthContainer)
      } else {
        createEventFailure(error)
      }
    }
  }

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }
    navigateToHome()
  }

  if (!appLookupComplete || !playableApp) return ""

  return (
    <Box sx={{ ...contentColumnPageShellSx, minHeight: 'calc(100svh - 128px)' }}>
      <CssBaseline />
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          p: 4,
          bgcolor: '#0B0F19',
          color: '#E6E9EF',
          minHeight: 'calc(100svh - 160px)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH
        }}
      >
        {/* Background gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'radial-gradient(1200px 200px at -5% 110%, rgba(124,58,237,0.35), transparent), radial-gradient(1000px 200px at 105% -10%, rgba(6,182,212,0.35), transparent)'
          }}
        />

        {/* Header section with title and icon + creator avatar overlay */}
        <Box sx={{ position: 'relative', zIndex: 1, mb: 4 }}>
          <PageBackRow onClick={handleBack} sx={{ mb: 1.5 }} />
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 0.2, mb: 3 }}>
            Create your {playableApp.shortName} Event
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'relative',
                width: 120,
                height: 120,
                float: 'left',
                mr: 3,
                mb: 1
              }}
            >
              <Avatar
                src={playableApp.iconUrl}
                alt={playableApp.shortName}
                variant="rounded"
                imgProps={{ loading: 'lazy' }}
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 3,
                  objectFit: 'cover',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.6)'
                }}
              />
              {showCreatorAvatarOverlay && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '-0.35rem',
                    right: '-0.85rem',
                    zIndex: 2,
                    border: '2px solid rgba(255,255,255,0.9)',
                    borderRadius: '50%',
                    height: 40,
                    width: 40,
                    boxShadow: '0 0 0 2px rgba(15,23,42,0.9)'
                  }}
                >
                  <Avatar
                    src={normalizeAvatarSrc(user.avatarUrl()) || undefined}
                    alt={user.name()}
                    sx={{
                      height: '100%',
                      width: '100%',
                      bgcolor: 'primary.main',
                      color: '#fff',
                      fontSize: '1.4rem',
                      fontWeight: 600
                    }}
                  >
                    {user.initial()}
                  </Avatar>
                </Box>
              )}
            </Box>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              {playableApp.description}
            </Typography>
            {playableApp.latestAppVersion && (
              <Typography variant="caption" sx={{ opacity: 0.55, display: 'block', mt: 0.75 }}>
                App version: {playableApp.latestAppVersion}
              </Typography>
            )}
            <Box sx={{ clear: 'both' }} />
          </Box>
        </Box>

        {/* Form section */}
        <Box
          component="form"
          sx={{
            position: 'relative',
            zIndex: 1,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '600px'
          }}
          onSubmit={handleSubmit}
        >
          <FormControl
            required
            fullWidth
            error={error !== null}
            sx={{
              mb: 2,
              '& label + .MuiInputBase-root': {
                marginTop: 0
              }
            }}
            variant="standard"
          >
            <InputLabel
              htmlFor="name"
              shrink={true}
              sx={{
                color: '#06B6D4',
                fontWeight: 700,
                fontSize: '0.875rem',
                transform: 'none',
                position: 'static',
                mb: 0.25
              }}
            >
              Event Name
            </InputLabel>
            <Input
              id="name"
              name="name"
              value={name || ""}
              autoComplete="name"
              required
              sx={{
                color: '#E6E9EF',
                marginTop: '0 !important',
                '&::before': {
                  borderBottom: '1px solid rgba(230,233,239,0.3)'
                },
                '&:hover:not(.Mui-disabled)::before': {
                  borderBottom: '1px solid rgba(230,233,239,0.5)'
                },
                '&::after': {
                  borderBottom: '2px solid #7C3AED'
                }
              }}
              onChange={e => {setName(e.target.value); setError(null)}}
            />
            {error && error.hasSource && error.source === 'name' && (
              <FormHelperText htmlFor="form-selector" error={error !== null} sx={{ mt: 0.5 }}>
                {error.message}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl
            required
            fullWidth
            error={error !== null}
            sx={{
              mb: 2,
              '& label + .MuiInputBase-root': {
                marginTop: 0
              }
            }}
            variant="standard"
          >
            <InputLabel
              htmlFor="description"
              shrink={true}
              sx={{
                color: '#06B6D4',
                fontWeight: 700,
                fontSize: '0.875rem',
                transform: 'none',
                position: 'static',
                mb: 0.25
              }}
            >
              Event Description
            </InputLabel>
            <Input
              id="description"
              name="description"
              value={description || ""}
              autoComplete="description"
              required
              multiline
              minRows={3}
              maxRows={7}
              sx={{
                color: '#E6E9EF',
                marginTop: '0 !important',
                alignItems: 'flex-start',
                '& .MuiInputBase-inputMultiline': {
                  overflowY: 'auto'
                },
                '&::before': {
                  borderBottom: '1px solid rgba(230,233,239,0.3)'
                },
                '&:hover:not(.Mui-disabled)::before': {
                  borderBottom: '1px solid rgba(230,233,239,0.5)'
                },
                '&::after': {
                  borderBottom: '2px solid #7C3AED'
                }
              }}
              onChange={e => {setDescription(e.target.value); setError(null)}}
            />
            {error && error.hasSource && error.source === 'description' && (
              <FormHelperText htmlFor="form-selector" error={error !== null} sx={{ mt: 0.5 }}>
                {error.message}
              </FormHelperText>
            )}
          </FormControl>

          {/* Display geolocation and other general errors above the submit button */}
          {error && (!error.hasSource || (error.source !== 'name' && error.source !== 'description')) && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#EF4444',
                  fontWeight: 500,
                  lineHeight: 1.5
                }}
              >
                {error.message || 'An error occurred. Please try again.'}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={disabledSubmitButton}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 999,
                px: 3,
                py: 1.5,
                fontSize: '1.1rem',
                background: 'linear-gradient(90deg,#7C3AED,#06B6D4)',
                boxShadow: '0 0 32px rgba(124,58,237,0.45)',
                '&:hover': {
                  background: 'linear-gradient(90deg,#6D28D9,#0891B2)',
                  boxShadow: '0 0 40px rgba(124,58,237,0.55)'
                },
                '&:disabled': {
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(230,233,239,0.5)'
                }
              }}
            >
              Play Now
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default CreateEventPage
